/**
 * Where each straight length of cabinet trim goes — the one rule the 3D view,
 * the elevation and the estimate all read, so what is drawn is what is billed
 * (claude-docs/2026-10-30-venus-full-line-design.md, "Where crown goes").
 *
 * Crown, its stacking and light rail run along every EXPOSED edge of the
 * cabinets carrying them, at their band (`bands.js`):
 *
 *   - an edge is exposed where nothing stands against it: not a wall (within
 *     `WALL_CONTACT_M`), and not another object reaching the trim's height —
 *     a cabinet of the same top, a taller pantry, a chimney. So a run between
 *     two walls gets one front piece; a free end wraps back to the wall; a
 *     12" upper beside the 24"-deep over-fridge unit gets a return along the
 *     deeper unit's side; a pantry taller than the crown stops it square, and
 *     wraps its own crown round its side above the uppers.
 *   - collinear pieces at one band, facing one way, with one profile and
 *     colour, are one piece;
 *   - where two pieces meet at an OUTSIDE corner both are mitred
 *     (`millwork/miter.js`); at an INSIDE corner both stop square at the
 *     corner (their profiles meet there, as a coped joint does).
 *
 * Each run (or each set of run-less cabinets sharing one choice) resolves its
 * profile once, over its carriers' colours (`resolveTrimProfile`): a profile
 * the line does not sell in every one of them is left out for the whole run,
 * with a note — never swapped.
 *
 * A piece's `start`/`end` are its BACK line, on the cabinet; its profile
 * stands `depthM` out along `facing`; `startExt`/`endExt` are how far it
 * overshoots each end for a mitre (0 = square), as `applyMiterCut` takes
 * them. Pure; metres.
 */
const { colourCodeFor, familySizes } = require('../cabinet-runs/vendorSizes');
const { isWoodHood } = require('../fixture-types/hoodForm');
const { woodHoodShapeOf } = require('../cabinet-runs/woodHoods');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { miterExtension } = require('../millwork/miter');
const {
  trimProfiles,
  resolveTrimProfile,
  trimFamilyFor,
} = require('./profiles');
const { isCrownCarrier, isLightRailCarrier } = require('./carriers');
const { microwaveShelfDepthM } = require('../cabinet-runs/microwaveCabinets');
const { crownBand, lightRailBand } = require('./bands');
const { carrierOutlines, outlineEdges } = require('./fronts');
const {
  cabinetValances,
  valanceCarriers,
  isValanceCarrier,
} = require('./valances');

/** A cabinet within this of a wall is against it — a scribe gap's worth. */
const WALL_CONTACT_M = 0.0254;
/** Heights and ends within this are the same (the strips' touch tolerance). */
const SAME_M = 0.01;
/** Pieces whose ends are this close meet. */
const JOIN_M = 0.002;
/**
 * A neighbour this close stands against an edge (the strips' touch
 * tolerance): a hair gap between two cabinets is no gap — no returns back to
 * back in it, and the front piece carries on across it.
 */
const GAP_M = SAME_M;
const MIN_PIECE_M = 0.005;
const PARALLEL_SIN = Math.sin((1 * Math.PI) / 180);

const topOf = (o) => o.center.y + o.dimensions.y / 2;
const bottomOf = (o) => o.center.y - o.dimensions.y / 2;
const hasGeometry = (o) =>
  o?.center &&
  o?.dimensions &&
  [o.center.x, o.center.y, o.center.z].every(Number.isFinite) &&
  o.dimensions.x > 0 &&
  o.dimensions.y > 0 &&
  o.dimensions.z > 0;

const snap = (v) => {
  if (Math.abs(v) < 1e-9) return 0;
  if (Math.abs(Math.abs(v) - 1) < 1e-9) return Math.sign(v);
  return v;
};
const dot = (a, b) => a.x * b.x + a.z * b.z;
const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const lerp = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  z: a.z + (b.z - a.z) * t,
});
const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
// the direction a piece runs, so the profile faces to its left
const runDir = (f) => ({ x: f.z, z: -f.x });

function pieceLengthM(piece) {
  return dist(piece.start, piece.end) + piece.startExt + piece.endExt;
}

// --- coverage --------------------------------------------------------------

/** Params along a→b where segment a→b crosses polygon edges. */
function crossings(a, b, polygon) {
  const out = [];
  const d = sub(b, a);
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i];
    const q = polygon[(i + 1) % polygon.length];
    const e = sub(q, p);
    const den = d.x * e.z - d.z * e.x;
    if (Math.abs(den) < 1e-12) continue;
    const w = sub(p, a);
    const t = (w.x * e.z - w.z * e.x) / den;
    const u = (w.x * d.z - w.z * d.x) / den;
    if (t > 0 && t < 1 && u >= -1e-9 && u <= 1 + 1e-9) out.push(t);
  }
  return out;
}

function closestOnSegment(m, s, e) {
  const d = sub(e, s);
  const len2 = dot(d, d);
  const t = len2 > 0 ? Math.max(0, Math.min(1, dot(sub(m, s), d) / len2)) : 0;
  return lerp(s, e, t);
}

/** Is this stretch of an edge against a wall (the wall on its outward side)? */
function againstWall(m, edgeDir, normal, walls) {
  return walls.some(({ start, end, dir }) => {
    if (Math.abs(edgeDir.x * dir.z - edgeDir.z * dir.x) > PARALLEL_SIN)
      return false;
    const c = closestOnSegment(m, start, end);
    return dist(c, m) <= WALL_CONTACT_M && dot(sub(c, m), normal) >= -1e-6;
  });
}

function wallParams(a, b, walls) {
  const d = sub(b, a);
  const len2 = dot(d, d);
  const ts = [];
  for (const w of walls)
    for (const p of [w.start, w.end]) {
      const t = dot(sub(p, a), d) / len2;
      if (t > 0 && t < 1) ts.push(t);
    }
  return ts;
}

/**
 * The exposed stretches of a cabinet's outline: each `{ a, b, normal, front }`
 * that no wall and no covering object stands against.
 */
function exposedStretches(carrier, covering, walls) {
  const out = [];
  for (const edge of outlineEdges(carrier)) {
    const { a, b, normal } = edge;
    const len = dist(a, b);
    if (len < MIN_PIECE_M) continue;
    const off = (p) => ({
      x: p.x + normal.x * GAP_M,
      z: p.z + normal.z * GAP_M,
    });
    const a1 = off(a);
    const b1 = off(b);
    const ts = [0, 1, ...wallParams(a, b, walls)];
    for (const poly of covering) ts.push(...crossings(a1, b1, poly));
    ts.sort((x, y) => x - y);
    const dir = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    let open = null;
    for (let i = 0; i < ts.length - 1; i++) {
      const [t0, t1] = [ts[i], ts[i + 1]];
      if ((t1 - t0) * len < 1e-6) continue;
      const tm = (t0 + t1) / 2;
      const covered =
        covering.some((poly) => isPointInsidePolygon(lerp(a1, b1, tm), poly)) ||
        againstWall(lerp(a, b, tm), dir, normal, walls);
      if (covered) {
        if (open) out.push(open);
        open = null;
      } else if (open) open.b = lerp(a, b, t1);
      else open = { ...edge, a: lerp(a, b, t0), b: lerp(a, b, t1) };
    }
    if (open) out.push(open);
  }
  return out.filter((s) => dist(s.a, s.b) >= MIN_PIECE_M);
}

// --- choices -----------------------------------------------------------------

// a filler or blank panel: no door, cut from the run's own stock
const isDoorless = (o) =>
  Boolean(o?.category?.filler) || o?.type === 'blank-panel';
// …and a Venus wood hood, which carries no colour of its own: it wears its
// run's cabinets' (`woodHoodLookOf`); and a valance, cut from the run's stock
const takesRunColour = (o) =>
  isDoorless(o) || isWoodHood(o) || isValanceCarrier(o);

/**
 * Each carrier's colour code. A doorless piece whose colour the line cannot
 * read on its own (a filler with no door design recorded) takes the colour
 * most of the run's cabinets wear: it is cut from that stock, and one
 * unreadable filler must not take the whole run's crown away. A wood hood
 * takes it the same way: it wears the run's colour. A valance's stand-in
 * takes the colour of the uppers it hangs between — on a two-tone run the
 * crown across it is theirs — and the run's only where theirs is unreadable.
 */
function carrierColours(family, carriers) {
  const read = carriers.map((c) => colourCodeFor(family, c));
  const byId = new Map(carriers.map((c, i) => [c.id, read[i]]));
  const own = carriers.map((c, i) =>
    isValanceCarrier(c)
      ? ((c.trimCarrierIds || []).map((id) => byId.get(id)).find(Boolean) ??
        null)
      : read[i]
  );
  const counts = new Map();
  own.forEach((c, i) => {
    if (c !== null && !takesRunColour(carriers[i]))
      counts.set(c, (counts.get(c) || 0) + 1);
  });
  const usual =
    [...counts.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    )[0]?.[0] ?? null;
  return own.map((c, i) =>
    c === null && takesRunColour(carriers[i]) ? usual : c
  );
}

const groupKey = (o, stamp, family) =>
  o.runId ? `run:${o.runId}` : `free:${family?.id}:${JSON.stringify(stamp)}`;

/**
 * Resolve each run's (or run-less set's) choice over its carriers' colours.
 * Returns `[{ carriers, family, profiles: { … } }]` and the notes for what was
 * left out.
 */
function resolveGroups(room, carriers, field, resolveProfiles) {
  const groups = new Map();
  for (const o of carriers) {
    const family = trimFamilyFor(room, o);
    const key = groupKey(o, o[field], family);
    if (!groups.has(key))
      groups.set(key, { family, stamp: o[field], carriers: [] });
    groups.get(key).carriers.push(o);
  }
  const resolved = [];
  const notes = [];
  for (const g of [...groups.values()]) {
    const codes = familySizes(g.family)
      ? carrierColours(g.family, g.carriers)
      : [];
    const { profiles, reasons } = resolveProfiles(
      g.family,
      g.stamp,
      codes.filter((c, i) => !isDoorless(g.carriers[i]) || c !== null)
    );
    for (const reason of reasons)
      notes.push({
        carrierId: g.carriers[0].trimCarrierIds?.[0] ?? g.carriers[0].id,
        runId: g.carriers[0].runId ?? null,
        reason,
      });
    if (profiles) resolved.push({ ...g, profiles, codes });
  }
  return { resolved, notes };
}

function crownProfiles(family, stamp, codes) {
  const crown = resolveTrimProfile(family, 'crown', stamp?.profile, codes);
  if (!crown.profile) return { profiles: null, reasons: [crown.reason] };
  if (!stamp.stacking)
    return { profiles: { crown: crown.profile, stacking: null }, reasons: [] };
  const stackingId = trimProfiles(family, 'stacking')[0]?.id;
  const stacking = resolveTrimProfile(family, 'stacking', stackingId, codes);
  return {
    profiles: { crown: crown.profile, stacking: stacking.profile },
    reasons: stacking.profile ? [] : [stacking.reason],
  };
}

function railProfiles(family, stamp, codes) {
  const rail = resolveTrimProfile(family, 'light-rail', stamp?.profile, codes);
  return rail.profile
    ? { profiles: { rail: rail.profile }, reasons: [] }
    : { profiles: null, reasons: [rail.reason] };
}

// --- assembling pieces -------------------------------------------------------

function rawPiece(stretch, kind, profile, colourCode, band, carrierIds) {
  const facing = { x: snap(stretch.normal.x), z: snap(stretch.normal.z) };
  const d = runDir(facing);
  const forward = dot(sub(stretch.b, stretch.a), d) >= 0;
  return {
    kind,
    profileId: profile.id,
    colourCode,
    start: forward ? stretch.a : stretch.b,
    end: forward ? stretch.b : stretch.a,
    facing,
    bottomY: band.bottomY,
    topY: band.topY,
    depthM: profile.depthM,
    // the profile's own face height — more than `topY - bottomY` where the
    // ceiling cuts it off
    heightM: profile.heightM,
    startExt: 0,
    endExt: 0,
    role: stretch.front ? 'front' : 'return',
    carrierIds,
  };
}

const sameRun = (p, q) =>
  p.kind === q.kind &&
  p.profileId === q.profileId &&
  p.colourCode === q.colourCode &&
  Math.abs(p.bottomY - q.bottomY) < SAME_M &&
  Math.abs(p.topY - q.topY) < SAME_M &&
  dot(p.facing, q.facing) > Math.cos((1 * Math.PI) / 180);

/** Does q carry straight on from p — on its line, across at most a hair gap? */
function continues(p, q) {
  const step = sub(q.start, p.end);
  return (
    Math.abs(dot(step, p.facing)) < 1e-3 &&
    dot(step, runDir(p.facing)) > -JOIN_M &&
    dist(p.end, q.start) <= GAP_M
  );
}

/** Join collinear pieces end to start into one. */
function mergeCollinear(pieces) {
  const list = [...pieces];
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = 0; j < list.length; j++) {
        if (i === j) continue;
        const p = list[i];
        const q = list[j];
        if (!sameRun(p, q) || !continues(p, q)) continue;
        list[i] = {
          ...p,
          end: q.end,
          role: p.role === 'front' || q.role === 'front' ? 'front' : 'return',
          carrierIds: [...new Set([...p.carrierIds, ...q.carrierIds])].sort(),
        };
        list.splice(j, 1);
        merged = true;
        break outer;
      }
    }
  }
  return list;
}

/** Mitre every outside corner; an inside corner stays square. */
function mitreCorners(pieces) {
  const out = pieces.map((p) => ({ ...p }));
  for (const p of out) {
    const q = out.find(
      (r) =>
        r !== p &&
        r.kind === p.kind &&
        Math.abs(r.bottomY - p.bottomY) < SAME_M &&
        dist(p.end, r.start) <= JOIN_M
    );
    if (!q) continue;
    const d1 = runDir(p.facing);
    const d2 = runDir(q.facing);
    const cos = Math.max(-1, Math.min(1, dot(d1, d2)));
    if (dot(p.facing, d2) >= 0) continue; // straight on, or an inside corner
    const deflection = Math.acos(cos);
    p.endExt = miterExtension(deflection, p.depthM);
    q.startExt = miterExtension(deflection, q.depthM);
  }
  return out;
}

const pieceOrder = (p, q) =>
  p.kind.localeCompare(q.kind) ||
  p.bottomY - q.bottomY ||
  p.start.x - q.start.x ||
  p.start.z - q.start.z ||
  p.end.x - q.end.x ||
  p.end.z - q.end.z;

// --- the rule ----------------------------------------------------------------

/**
 * The cabinet as its light rail sees it: a microwave shelf cabinet's shelf
 * stands out past its doors (`microwaveShelfDepthM`, 15¾" against the box's
 * 12"), so its rail runs along the shelf's front edge — the same cabinet, as
 * deep as its shelf, its back still on the wall. Any other carrier as it is.
 */
function railOutlineOf(o) {
  if (!o.category?.microwaveShelf) return o;
  const depth = microwaveShelfDepthM();
  const grow = depth - o.dimensions.z;
  if (!(grow > 0)) return o;
  const ry = o.rotation?.y || 0;
  return {
    ...o,
    center: {
      ...o.center,
      x: o.center.x + (grow / 2) * Math.sin(ry),
      z: o.center.z + (grow / 2) * Math.cos(ry),
    },
    dimensions: { ...o.dimensions, z: depth },
  };
}

/** A carrier with another cabinet standing on its top (a stacked row). */
function hasCabinetOnTop(carrier, objects) {
  const outlines = carrierOutlines(carrier);
  const top = topOf(carrier);
  return objects.some(
    (o) =>
      o !== carrier &&
      Math.abs(bottomOf(o) - top) < SAME_M &&
      outlines.some((poly) => isPointInsidePolygon(o.center, poly))
  );
}

function wallSegments(room) {
  return (room?.walls || [])
    .map(getWallAxes)
    .filter(Boolean)
    .map(({ start, end, wallDir }) => ({ start, end, dir: wallDir }));
}

/**
 * Pieces for one kind of trim. `entries`: `{ carrier, band, parts:
 * [{ kind, profile, band }], colourCode }`. What stands in a carrier's way:
 * another carrier with this trim at the same band (their trim meets), or an
 * object `reaches(o, band)` — one that rises into the band itself. An object
 * that merely stops at the cabinets' line — a hood's box under the uppers'
 * top — does not, so the crown returns beside it.
 */
function piecesFor(entries, objects, walls, reaches) {
  const raw = [];
  for (const e of entries) {
    const sameBand = new Set(
      entries
        .filter(
          (f) => f !== e && Math.abs(f.band.bottomY - e.band.bottomY) < SAME_M
        )
        .map((f) => f.carrier)
    );
    const covering = objects
      .filter((o) => o !== e.carrier && (sameBand.has(o) || reaches(o, e.band)))
      .flatMap(carrierOutlines);
    for (const stretch of exposedStretches(e.carrier, covering, walls))
      for (const part of e.parts)
        if (part.band.topY - part.band.bottomY > 1e-6)
          raw.push(
            rawPiece(
              stretch,
              part.kind,
              part.profile,
              e.colourCode,
              part.band,
              // a valance reports the uppers it hangs between
              e.carrier.trimCarrierIds || [e.carrier.id]
            )
          );
  }
  return mitreCorners(mergeCollinear(raw));
}

/**
 * The crown (and stacking) each carrier gets: `{ entries, notes }`, one entry
 * per carrier with a drawn crown — `{ carrier, band, parts, colourCode }`.
 */
function crownEntriesFor(room, objects, ceilingY) {
  const crownCarriers = objects.filter(
    (o) => isCrownCarrier(o) && o.cabinetCrown && !hasCabinetOnTop(o, objects)
  );
  const crown = resolveGroups(
    room,
    crownCarriers,
    'cabinetCrown',
    crownProfiles
  );
  const entries = [];
  for (const g of crown.resolved)
    g.carriers.forEach((carrier, i) => {
      const band = crownBand(carrier, g.profiles, ceilingY);
      if (!band) return;
      const parts = [
        {
          kind: 'crown',
          profile: g.profiles.crown,
          band: { bottomY: band.stackingTopY, topY: band.topY },
        },
      ];
      if (g.profiles.stacking)
        parts.push({
          kind: 'stacking',
          profile: g.profiles.stacking,
          band: { bottomY: band.bottomY, topY: band.stackingTopY },
        });
      entries.push({
        carrier,
        band,
        parts,
        colourCode: g.codes[i] ?? null,
      });
    });
  return { entries, notes: crown.notes };
}

/**
 * The room's objects, and each valance over a sink as a crown carrier
 * (`valances.valanceCarriers`) — the crown runs straight across it. None
 * where a stacked row stands on its uppers: their crown is the stack's, up
 * on top of it.
 */
function withValanceCarriers(room) {
  const objects = (room?.objects || []).filter(hasGeometry);
  const byId = new Map(objects.map((o) => [o.id, o]));
  const { valances } = cabinetValances(room);
  const crowned = valanceCarriers(room, valances).filter((c) =>
    c.trimCarrierIds.every((id) => {
      const upper = byId.get(id);
      return upper && !hasCabinetOnTop(upper, objects);
    })
  );
  return [...objects, ...crowned];
}

/**
 * Each carrier's crown top by id — where its crown (stacking included) ends,
 * for the wall crown's stop rule (`millwork/crown.js`). A cabinet whose crown
 * is left out, or has no gap above it, is absent.
 */
function crownTopsById(room, { ceilingY }) {
  const objects = withValanceCarriers(room);
  const { entries } = crownEntriesFor(room, objects, ceilingY);
  return new Map(entries.map((e) => [e.carrier.id, e.band.topY]));
}

/**
 * @param {Object} room  `objects` (stamped `cabinetCrown` / `lightRail`),
 *   `walls`, `cabinetRuns`
 * @param {{ ceilingY: number }} opts
 * @returns {{ pieces: TrimPiece[], notes: { carrierId, runId, reason }[] }}
 */
function cabinetTrimPieces(room, { ceilingY }) {
  const objects = withValanceCarriers(room).sort((a, b) =>
    String(a.id).localeCompare(String(b.id))
  );
  const walls = wallSegments(room);

  const crown = crownEntriesFor(room, objects, ceilingY);
  const crownEntries = crown.entries;
  // an object stands in the crown's way when it rises into the crown — not
  // a curved wood hood, which is inches narrower than its box up there (its
  // chimney is 9" narrower than its band), so the crown beside it returns to
  // the wall
  const reachesCrown = (o, band) =>
    woodHoodShapeOf(o) !== 'curved' &&
    topOf(o) > band.bottomY + SAME_M &&
    bottomOf(o) < band.topY - SAME_M;

  const railCarriers = objects
    .filter((o) => isLightRailCarrier(o) && o.lightRail)
    .map(railOutlineOf);
  const rail = resolveGroups(room, railCarriers, 'lightRail', railProfiles);
  const railEntries = [];
  for (const g of rail.resolved)
    g.carriers.forEach((carrier, i) => {
      const band = lightRailBand(carrier, g.profiles.rail);
      railEntries.push({
        carrier,
        band,
        parts: [{ kind: 'light-rail', profile: g.profiles.rail, band }],
        colourCode: g.codes[i] ?? null,
      });
    });
  // …and in the light rail's when it hangs down into the rail
  const reachesRail = (o, band) =>
    bottomOf(o) < band.topY - SAME_M && topOf(o) > band.bottomY + SAME_M;

  const pieces = [
    ...piecesFor(crownEntries, objects, walls, reachesCrown),
    ...piecesFor(railEntries, objects, walls, reachesRail),
  ].sort(pieceOrder);
  return { pieces, notes: [...crown.notes, ...rail.notes] };
}

module.exports = {
  WALL_CONTACT_M,
  cabinetTrimPieces,
  // the open stretches of a cabinet's outline and the walls they are judged
  // against — the wainscot end panels' test too (`endPanels.js`)
  exposedStretches,
  wallSegments,
  // the colours a run's trim is judged by — the panel greys by the same ones
  trimColoursFor: carrierColours,
  crownTopsById,
  pieceLengthM,
};
